import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "src/config/axios";
import { Form, Formik } from "formik";
import FormInputText from "src/components/Forms/FormInputText";
import moment from "moment";
import {
  showErrorToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import { DashboardContext } from "src/routes/DashboardRoutes";

const CreateBookingQuoteDialog = ({ booking, handleClose, handleSubmit }) => {
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState({});
  const [ageGroups, setAgeGroups] = useState();
  const formRef = useRef();
  const { t } = useTranslation("dashboard");
  const { settings } = useContext(DashboardContext);
  const dogsEnabled = settings?.business?.dogs_enabled;

  async function loadBookingQuote() {
    return await axios.get(`/api/v1/bookings/${booking.id}/pricing/quote.json`);
  }

  async function loadAgeGroups() {
    return await axios.get(`/api/v1/age_groups.json`);
  }

  function fillForm() {
    const formValues = { ...quote };

    if (booking.parking_slot_id) {
      formValues.parking_slot_id = booking.parking_slot_id;
    }

    if (formValues.from) {
      formValues.from = moment(formValues.from).format(moment.HTML5_FMT.DATE);
    }

    if (formValues.to) {
      formValues.to = moment(formValues.to).format(moment.HTML5_FMT.DATE);
    }

    formValues.dogs = dogsEnabled ? booking?.amount_dogs : 0;

    quote.age_group_counts.forEach((ageGroupCount) => {
      formValues[`age_group_${ageGroupCount.id}`] = ageGroupCount.amount;
    });

    return formValues;
  }

  async function loadQuoteTemplate(formValues) {
    const values = { ...formValues };

    const ageGroupCounts = ageGroups.entries.map((ageGroup) => ({
      id: ageGroup.id,
      amount: Number(values[`age_group_${ageGroup.id}`] || 0) || 0,
    }));

    const params = {
      from: values.from,
      to: values.to,
      nights: values.nights,
      dogs: dogsEnabled ? values.dogs : 0,
      booking_id: booking?.id,
      parking_slot_id: values.parking_slot_id,
      age_group_counts: ageGroupCounts,
    };

    return await axios.post("/api/v1/pricing/template.json", params);
  }

  async function submit(values, helpers) {
    helpers.setSubmitting(true);

    const quoteTemplateResponse = await loadQuoteTemplate(values);
    const quoteTemplate = quoteTemplateResponse.data;

    const invoiceResponse = await axios.post(
      `/api/v1/bookings/${booking.id}/invoices.json`
    );
    const invoice = invoiceResponse.data;

    await axios
      .post(
        `/api/v1/invoices/${invoice.id}/create_invoice_items_by_quote.json`,
        { quote: quoteTemplate }
      )
      .then(({ data }) => {
        handleSubmit(data);
      })
      .catch((e) => {
        if (e.response.data) {
          showValidationErrorToast(e?.response?.data);
        } else {
          showErrorToast();
        }
      });
  }

  function handleFromChange(e, values, setFieldValue) {
    const shouldValidate = true;
    const from = e.target.value;
    setFieldValue("from", from, shouldValidate);
    setFieldValue("nights", moment(values.to).diff(moment(from), "days"));
  }

  function handleNightsChange(e, values, setFieldValue) {
    const shouldValidate = true;
    const to = moment(values.from)
      .add(e.target.value, "days")
      .format(moment.HTML5_FMT.DATE);
    setFieldValue("nights", e.target.value);
    setFieldValue("to", to, shouldValidate);
  }

  function handleToChange(e, values, setFieldValue) {
    const shouldValidate = true;
    const to = e.target.value;
    setFieldValue("to", to, shouldValidate);
    setFieldValue(
      "nights",
      moment(to).diff(moment(values.from), "days"),
      "days"
    );
  }

  useEffect(() => {
    async function init() {
      const response = await loadAgeGroups();
      setAgeGroups(response.data);

      if (booking) {
        const bookingQuoteResponse = await loadBookingQuote().catch((e) => {
          console.error(e);
        });
        setQuote(bookingQuoteResponse.data);
      }

      setLoading(false);
    }

    const _ = init();
  }, []);

  return (
    <>
      <Modal
        show={true}
        onHide={handleClose}
        dialogClassName={"invoice-payments-dialog"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("booking_details.create_booking_offer_dialog.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            {t("booking_details.create_booking_offer_dialog.description")}
          </p>
          {loading ? (
            <Spinner
              className={"d-block mx-auto"}
              animation="border"
              variant="primary"
            />
          ) : (
            <Formik
              innerRef={formRef}
              initialValues={fillForm()}
              enableReinitialize={true}
              onSubmit={submit}
            >
              {({ values, setFieldValue }) => (
                <Form autoComplete="false">
                  <div className={"d-flex"}>
                    <div className={"mr-2 flex-grow-1"}>
                      <FormInputText
                        name={"from"}
                        label={t(
                          "booking_details.create_booking_offer_dialog.from.label"
                        )}
                        type={"date"}
                        onChange={(e) =>
                          handleFromChange(e, values, setFieldValue)
                        }
                      />
                    </div>
                    <div className={"mr-2"}>
                      <FormInputText
                        name={"nights"}
                        label={t(
                          "booking_details.create_booking_offer_dialog.nights.label"
                        )}
                        type={"number"}
                        onChange={(e) =>
                          handleNightsChange(e, values, setFieldValue)
                        }
                      />
                    </div>
                    <div className={"mr-2 flex-grow-1"}>
                      <FormInputText
                        name={"to"}
                        label={t(
                          "booking_details.create_booking_offer_dialog.to.label"
                        )}
                        type={"date"}
                        onChange={(e) =>
                          handleToChange(e, values, setFieldValue)
                        }
                      />
                    </div>
                  </div>
                  <div className={"d-flex"}>
                    {ageGroups.entries.map((ageGroup) => (
                      <div className={"mr-2"} key={ageGroup.id}>
                        <FormInputText
                          name={`age_group_${ageGroup.id}`}
                          label={t(ageGroup.name)}
                          hint={t("editStay.stay_age_groups.hint")}
                          type={"number"}
                        />
                      </div>
                    ))}
                    {dogsEnabled && (
                      <div className={"mr-2"}>
                        <FormInputText
                          name={"dogs"}
                          label={t(
                            "booking_details.create_booking_offer_dialog.amount_dogs.label"
                          )}
                          type={"number"}
                        />
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("common:components.button.close")}
          </Button>
          <Button
            type="submit"
            onClick={() => formRef.current.handleSubmit()}
            disabled={formRef?.current?.isSubmitting}
          >
            {t("booking_details.create_booking_offer_dialog.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateBookingQuoteDialog;
